import { formatMoney } from './novicell_shopify-utility.js';
import { populateUpsellDrawer, openUpsellDrawer } from './novicell_upsell-shared.js';

(() => {
  const moneyFormat = window.shopMoneyFormat;
  let carouselContainer;
  let upsellContainer;

  const recommendationsCache = new Map();
  let lastRenderedProductIds = '';

  async function fetchRecommendations(productId) {
    if (recommendationsCache.has(productId)) {
      return recommendationsCache.get(productId);
    }
    try {
      const response = await fetch(`/recommendations/products.json?product_id=${productId}&intent=complementary&limit=4`);
      if (!response.ok) return [];
      const data = await response.json();
      const products = data.products || [];
      recommendationsCache.set(productId, products);
      return products;
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      return [];
    }
  }

  async function getCartProducts() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return [];
      const cart = await response.json();
      return cart.items || [];
    } catch (error) {
      console.error('Error fetching cart:', error);
      return [];
    }
  }

  async function initCartUpsell(force = false) {
    carouselContainer = document.getElementById('cart-upsell-carousel');
    upsellContainer = document.getElementById('cart-upsell-container');

    if (!carouselContainer || !upsellContainer) return;

    // Only run if the drawer is active
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && !cartDrawer.classList.contains('active')) return;

    const cartItems = await getCartProducts();
    const productIds = [...new Set(cartItems.map(item => item.product_id))];
    const currentProductIdsString = productIds.sort().join(',');

    // Skip if the cart hasn't changed since last render, UNLESS forced
    if (!force && currentProductIdsString === lastRenderedProductIds && carouselContainer.innerHTML !== '') return;

    lastRenderedProductIds = currentProductIdsString;

    if (cartItems.length === 0) {
      upsellContainer.classList.add('tw-hidden');
      return;
    }
    const recommendationsPromises = productIds.map(id => fetchRecommendations(id));
    const recommendationsResults = await Promise.all(recommendationsPromises);

    // Flatten and unique by product ID
    const allRecommendations = recommendationsResults.flat();
    const uniqueRecommendations = [];
    const seenIds = new Set();

    // Also skip products already in cart
    const cartProductIds = new Set(productIds);

    allRecommendations.forEach(product => {
      if (!seenIds.has(product.id) && !cartProductIds.has(product.id)) {
        uniqueRecommendations.push(product);
        seenIds.add(product.id);
      }
    });

    if (uniqueRecommendations.length === 0) {
      upsellContainer.classList.add('tw-hidden');
      return;
    }

    renderRecommendations(uniqueRecommendations);
    upsellContainer.classList.remove('tw-hidden');
    initCarouselNavigation();
  }

  function escapeHTML(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function renderRecommendations(products) {
    if (!carouselContainer) return;

    carouselContainer.innerHTML = products.map(product => {
      const price = formatMoney(product.price, moneyFormat);
      const featuredImage = product.featured_image || '';

      const parts = product.title.split(',');
      const primaryTitle = escapeHTML(parts[0].trim());
      const secondaryTitle = parts.length > 1 ? escapeHTML(parts.slice(1).join(',').trim()) : "";
      const escapedVendor = escapeHTML(product.vendor || "");
      const escapedTitle = escapeHTML(product.title || "");

      // Escape title for data attribute
      const escapedProduct = JSON.stringify(product).replace(/'/g, "&#39;");

      return `
        <div class="cart-upsell-card tw-flex-shrink-0 tw-w-[var(--cart-upsell-card-width)] max-md:tw-max-w-[90%] tw-bg-[#E5E2D8] tw-p-4 tw-flex tw-gap-4">
          <div class="cart-upsell-card-image tw-w-[75px] tw-h-full tw-flex-shrink-0 tw-bg-white">
            <img src="${featuredImage}&width=200" alt="${escapedTitle}" class="tw-w-full tw-h-full tw-object-cover">
          </div>
          <div class="cart-upsell-card-info tw-flex tw-flex-col tw-justify-between tw-flex-grow">
            <div class="tw-flex tw-justify-between tw-items-start tw-gap-2">
              <div>
                <h4 class="tw-text-body-2 tw-font-primary tw-font-normal tw-m-0">${primaryTitle}</h4>
                <p class="tw-text-body-3 tw-font-secondary tw-m-0 tw-opacity-70">${secondaryTitle || escapedVendor}</p>
              </div>
              <p class="tw-text-body-3 tw-whitespace-nowrap tw-m-0">${price}</p>
            </div>
            <div class="tw-text-right">
              <button type="button" 
                      class="cart-upsell-add-btn tw-mt-2 tw-text-[15px] tw-bg-[#2F2B23] tw-text-white tw-py-3 tw-px-8 tw-font-secondary"
                      data-product='${escapedProduct}'>
                ${window.variantStrings?.addToCart || 'Add to cart'}
              </button>
            </div>
          </div>
        </div>
      `;
    }).join('');

    // Add event listeners to buttons
    carouselContainer.querySelectorAll('.cart-upsell-add-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        // Prevent click if we were dragging
        if (carouselContainer.dataset.preventClick === 'true') {
          return;
        }
        const productData = JSON.parse(btn.getAttribute('data-product'));
        populateUpsellDrawer(productData, moneyFormat);
        openUpsellDrawer();
      });
    });
  }

  function initCarouselNavigation() {
    const prevBtn = document.querySelector('.cart-upsell-prev');
    const nextBtn = document.querySelector('.cart-upsell-next');

    if (!prevBtn || !nextBtn) return;

    function updateNavButtons() {
      const atStart = carouselContainer.scrollLeft <= 0;
      const atEnd = carouselContainer.scrollLeft + carouselContainer.clientWidth >= carouselContainer.scrollWidth - 1;
      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;
    }

    function cardWidth() {
      const card = carouselContainer.querySelector('.cart-upsell-card');
      return card ? card.offsetWidth : 200;
    }

    nextBtn.onclick = () => {
      carouselContainer.scrollBy({ left: cardWidth(), behavior: 'smooth' });
    };

    prevBtn.onclick = () => {
      carouselContainer.scrollBy({ left: -cardWidth(), behavior: 'smooth' });
    };

    carouselContainer.addEventListener('scroll', updateNavButtons);
    updateNavButtons();

    initDragToScroll();
  }

  function initDragToScroll() {
    let isDown = false;
    let startX;
    let scrollLeft;
    let hasMoved = false;

    carouselContainer.addEventListener('mousedown', (e) => {
      isDown = true;
      hasMoved = false;
      carouselContainer.dataset.preventClick = 'false';
      carouselContainer.classList.add('is-dragging');
      startX = e.pageX - carouselContainer.offsetLeft;
      scrollLeft = carouselContainer.scrollLeft;
    });

    carouselContainer.addEventListener('mouseleave', () => {
      isDown = false;
      carouselContainer.classList.remove('is-dragging');
    });

    carouselContainer.addEventListener('mouseup', () => {
      isDown = false;
      carouselContainer.classList.remove('is-dragging');

      if (hasMoved) {
        carouselContainer.dataset.preventClick = 'true';
        requestAnimationFrame(() => {
          carouselContainer.dataset.preventClick = 'false';
        });
      }
    });

    carouselContainer.addEventListener('mousemove', (e) => {
      if (!isDown) return;

      const x = e.pageX - carouselContainer.offsetLeft;
      const walk = (x - startX) * 2; // scroll-fast

      if (Math.abs(x - startX) > 5) {
        hasMoved = true;
        carouselContainer.dataset.preventClick = 'true';
        e.preventDefault();
        carouselContainer.scrollLeft = scrollLeft - walk;
      }
    });
  }

  // Monitor cart-drawer opening
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (mutation.target.classList.contains('active')) {
            initCartUpsell();
          }
        }
      });
    });
    observer.observe(cartDrawer, { attributes: true });
  }

  // Re-initialize when cart drawer items update but ONLY if drawer is open
  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.classList.contains('active')) {
        requestAnimationFrame(() => initCartUpsell(true));
      }
    });
  }

  // Fallback for themes that don't use pubsub or for our specific refresh logic
  document.addEventListener('cart-drawer:updated', () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('active')) {
      initCartUpsell(true);
    }
  });
})();
